import type { EChartsOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import { buildSharedChrome, pickColor } from './chart-option-chrome';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';

/**
 * Input for {@link createGaugeChartOptions}.
 *
 * @example
 * const input: GaugeChartInput = { value: 72, name: 'Completeness', max: 100 };
 */
export type GaugeChartInput = {
  value: number;
  name?: string;
  min?: number;
  max?: number;
  /** Optional progress color stops as `[ratio, color]` pairs. */
  colorStops?: readonly (readonly [number, string])[];
};

/**
 * Generate an ECharts gauge for a single progress metric.
 *
 * @param input Gauge value and bounds. {@link GaugeChartInput}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A bare {@link EChartsOption}.
 * @example
 * const opt = createGaugeChartOptions({ value: 72, name: 'Completeness' });
 */
export function createGaugeChartOptions(
  input: GaugeChartInput,
  buildOptions: ChartOptionBuildOptions = {}
): EChartsOption {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const colorStops: [number, string][] = input.colorStops
    ? input.colorStops.map(([ratio, color]) => [ratio, color])
    : [
        [0.3, pickColor(theme.palette, 5)],
        [0.7, pickColor(theme.palette, 1)],
        [1, pickColor(theme.palette, 2)],
      ];

  return {
    ...buildSharedChrome(theme, buildOptions.showToolbox === true),
    series: [
      {
        type: 'gauge',
        min: input.min ?? 0,
        max: input.max ?? 100,
        progress: { show: true, width: 14 },
        axisLine: {
          lineStyle: {
            width: 14,
            color: colorStops,
          },
        },
        axisTick: { show: false },
        splitLine: {
          length: 8,
          lineStyle: { width: 2, color: theme.borderColor },
        },
        axisLabel: { color: theme.mutedColor, distance: 18 },
        pointer: { length: '60%', width: 5 },
        detail: {
          valueAnimation: true,
          formatter: '{value}%',
          color: theme.textColor,
          fontSize: 22,
        },
        title: { color: theme.mutedColor, offsetCenter: [0, '70%'] },
        data: [{ value: input.value, name: input.name ?? 'Value' }],
      },
    ],
  };
}
